import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Loader, Modal, Breadcrumb, Input, Checkbox } from 'semantic-ui-react';
import { useSelector } from 'react-redux';

import Config from '../../../constants/Config';
import selectors from '../../../selectors';

import styles from './GoogleDriveModal.module.scss';

const KB_ACCEPTED_MIME_TYPES = new Set([
  // Gemini-native
  'application/pdf',
  'text/plain',
  'text/html',
  'text/css',
  'text/javascript',
  'application/javascript',
  'application/x-javascript',
  'text/x-typescript',
  'text/csv',
  'text/markdown',
  'text/xml',
  'application/xml',
  'text/rtf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  // Google Workspace (converted to PDF)
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.google-apps.presentation',
  // Office (converted to PDF via Drive)
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
]);

const GoogleDriveModal = React.memo(
  ({ onClose, onSelect, allowMultiple = false, forKnowledgeBase = false }) => {
    const accessToken = useSelector(selectors.selectAccessToken);

    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentFolder, setCurrentFolder] = useState(null); // null = root
    const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'My Drive' }]);
    const [selectedFile, setSelectedFile] = useState(null); // Single select
    const [selectedFiles, setSelectedFiles] = useState([]); // Multi select
    const [searchQuery, setSearchQuery] = useState(''); // New state for search

    const fetchFiles = useCallback(
      async (folderId = null, query = '') => {
        setLoading(true);
        setError(null);
        try {
          let url = `${Config.SERVER_BASE_URL}/api/integrations/google/files`;
          const params = new URLSearchParams();

          if (folderId && !query) {
            // Only use folderId if not searching globally
            params.append('folderId', folderId);
          }

          if (query) {
            params.append('q', query);
          }

          const response = await fetch(`${url}?${params.toString()}`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });

          if (!response.ok) {
            throw new Error('Failed to fetch files');
          }

          const data = await response.json();
          setFiles(data.files || []);
        } catch (err) {
          console.error('Error fetching Drive files:', err);
          setError('Failed to load files from Google Drive');
        } finally {
          setLoading(false);
        }
      },
      [accessToken]
    );

    useEffect(() => {
      // Debounce search or fetch immediately if no query
      const timeoutId = setTimeout(
        () => {
          fetchFiles(currentFolder, searchQuery);
        },
        searchQuery ? 500 : 0
      );

      return () => clearTimeout(timeoutId);
    }, [fetchFiles, currentFolder, searchQuery]);

    const handleSearchChange = e => {
      setSearchQuery(e.target.value);
      if (!allowMultiple) setSelectedFile(null);
    };

    const handleFolderClick = folder => {
      setCurrentFolder(folder.id);
      setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }]);
      if (!allowMultiple) setSelectedFile(null);
      setSearchQuery(''); // Clear search when navigating into a folder
    };

    const handleBreadcrumbClick = index => {
      const target = breadcrumbs[index];
      setCurrentFolder(target.id);
      setBreadcrumbs(breadcrumbs.slice(0, index + 1));
      if (!allowMultiple) setSelectedFile(null);
    };

    const isFileSupported = file => {
      if (file.mimeType === 'application/vnd.google-apps.folder') return true;
      if (!forKnowledgeBase) return true;
      return KB_ACCEPTED_MIME_TYPES.has(file.mimeType);
    };

    const handleFileClick = file => {
      if (file.mimeType === 'application/vnd.google-apps.folder') {
        handleFolderClick(file);
      } else if (!isFileSupported(file)) {
        return;
      } else {
        if (allowMultiple) {
          setSelectedFiles(prev => {
            const exists = prev.find(f => f.id === file.id);
            if (exists) {
              return prev.filter(f => f.id !== file.id);
            }
            return [...prev, file];
          });
        } else {
          setSelectedFile(file);
        }
      }
    };

    const handleFileDoubleClick = file => {
      if (!isFileSupported(file)) return;
      if (file.mimeType !== 'application/vnd.google-apps.folder') {
        if (allowMultiple) {
          handleFileClick(file);
        } else {
          onSelect(file);
          onClose();
        }
      }
    };

    const handleSelect = async () => {
      if (allowMultiple) {
        if (selectedFiles.length === 0) return;
        onSelect(selectedFiles);
      } else {
        if (!selectedFile) return;
        onSelect(selectedFile);
      }
      onClose();
    };

    const getIcon = mimeType => {
      if (mimeType === 'application/vnd.google-apps.folder') return 'folder';
      if (mimeType.includes('image')) return 'image';
      if (mimeType.includes('pdf')) return 'file pdf';
      if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'file excel';
      if (mimeType.includes('document') || mimeType.includes('word')) return 'file word';
      return 'file outline';
    };

    const isSelected = file => {
      if (allowMultiple) {
        return selectedFiles.some(f => f.id === file.id);
      }
      return selectedFile?.id === file.id;
    };

    return (
      <Modal open closeIcon size="large" onClose={onClose}>
        <Modal.Header>Select from Google Drive</Modal.Header>
        <Modal.Content scrolling>
          <div className={styles.wrapper}>
            <div className={styles.header}>
              <div className={styles.breadcrumbsContainer}>
                <Breadcrumb>
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={crumb.id || 'root'}>
                      <Breadcrumb.Section
                        link
                        onClick={() => handleBreadcrumbClick(index)}
                        active={index === breadcrumbs.length - 1}
                      >
                        {crumb.name}
                      </Breadcrumb.Section>
                      {index < breadcrumbs.length - 1 && <Breadcrumb.Divider icon="right angle" />}
                    </React.Fragment>
                  ))}
                </Breadcrumb>
              </div>
              <div className={styles.searchContainer}>
                <Input
                  icon="search"
                  placeholder="Search in Drive..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  size="small"
                />
              </div>
            </div>

            {forKnowledgeBase && (
              <div className={styles.formatNotice}>
                <Icon name="info circle" />
                <span>
                  Accepted formats: PDF, Word (.doc/.docx), Excel (.xls/.xlsx), PowerPoint
                  (.ppt/.pptx), Google Docs/Sheets/Slides, Text, HTML, CSV, Markdown, XML, RTF,
                  Images (JPG, PNG, GIF, WebP)
                </span>
              </div>
            )}

            <div className={styles.fileList}>
              {loading ? (
                <div className={styles.loader}>
                  <Loader active inline="centered" />
                </div>
              ) : error ? (
                <div className={styles.emptyState}>
                  <Icon name="warning circle" color="red" />
                  <p>{error}</p>
                </div>
              ) : files.length === 0 ? (
                <div className={styles.emptyState}>
                  <Icon name="folder open outline" />
                  <p>This folder is empty</p>
                </div>
              ) : (
                files.map(file => {
                  const supported = isFileSupported(file);
                  return (
                    <div
                      key={file.id}
                      className={`${styles.fileItem} ${isSelected(file) ? styles.selected : ''} ${
                        !supported ? styles.disabled : ''
                      }`}
                      onClick={() => handleFileClick(file)}
                      onDoubleClick={() => handleFileDoubleClick(file)}
                      title={
                        !supported ? 'This file format is not supported for Knowledge Base' : ''
                      }
                    >
                      <div className={styles.fileIcon}>
                        {allowMultiple && file.mimeType !== 'application/vnd.google-apps.folder' ? (
                          <Checkbox
                            checked={isSelected(file)}
                            disabled={!supported}
                            style={{ pointerEvents: 'none' }}
                          />
                        ) : file.iconLink ? (
                          <img
                            src={file.iconLink}
                            alt=""
                            style={!supported ? { filter: 'grayscale(100%)' } : {}}
                          />
                        ) : (
                          <Icon name={getIcon(file.mimeType)} size="large" color="grey" />
                        )}
                      </div>
                      <div className={styles.fileInfo}>
                        <div className={styles.fileName}>
                          {file.name}
                          {!supported && (
                            <span className={styles.unsupportedBadge}>Unsupported</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className={styles.actions}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                primary
                disabled={allowMultiple ? selectedFiles.length === 0 : !selectedFile}
                onClick={handleSelect}
              >
                {allowMultiple ? `Select Files (${selectedFiles.length})` : 'Select File'}
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal>
    );
  }
);

GoogleDriveModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  allowMultiple: PropTypes.bool,
  forKnowledgeBase: PropTypes.bool,
};

export default GoogleDriveModal;
